import React, {Fragment, useEffect, useRef, useState} from 'react';
import {Menu, Transition} from "@headlessui/react";
import {ArrowTurnDownRightIcon, EllipsisVerticalIcon} from "@heroicons/react/24/solid";
import {ICategory} from "../../model/ICategory";
import {classNames} from "../../utils/common";
import {DropPlacement} from "./categoryTreeUtils";

interface CategoryTreeItemProps {
    category: ICategory;
    dropPlacement: DropPlacement | null;
    isDragging: boolean;
    highlighted: boolean;
    showRevert: boolean;
    onDragStart: (category: ICategory) => void;
    onDragOver: (category: ICategory, event: React.DragEvent<HTMLTableRowElement>) => void;
    onDrop: (category: ICategory, event: React.DragEvent<HTMLTableRowElement>) => void;
    onDragEnd: () => void;
    onRename: (category: ICategory, name: string) => Promise<boolean>;
    onDelete: (category: ICategory) => void;
    onRevert: () => void;
}

const CategoryTreeItem: React.FunctionComponent<CategoryTreeItemProps> = ({
    category,
    dropPlacement,
    isDragging,
    highlighted,
    showRevert,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    onRename,
    onDelete,
    onRevert,
}) => {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(category.name);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setName(category.name);
    }, [category.name]);

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    function cancelEdit() {
        setName(category.name);
        setEditing(false);
    }

    async function saveEdit() {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setName(category.name);
            setEditing(false);
            return;
        }
        if (trimmedName === category.name) {
            setEditing(false);
            return;
        }
        setSaving(true);
        const saved = await onRename(category, trimmedName);
        setSaving(false);
        if (saved) {
            setEditing(false);
        }
    }

    return (
        <tr
            data-category-id={category.id}
            tabIndex={-1}
            draggable={!editing}
            onDragStart={(event) => {
                event.dataTransfer.setData('text/plain', String(category.id));
                event.dataTransfer.effectAllowed = 'move';
                onDragStart(category);
            }}
            onDragOver={(event) => onDragOver(category, event)}
            onDrop={(event) => onDrop(category, event)}
            onDragEnd={onDragEnd}
            className={classNames(
                'account-row-focus',
                highlighted ? 'account-row-highlight' : '',
                isDragging ? 'opacity-50' : '',
                dropPlacement === 'before' ? 'category-drop-before' : '',
                dropPlacement === 'after' ? 'category-drop-after' : '',
                dropPlacement === 'into' ? 'category-drop-into' : '',
                editing ? '' : 'cursor-grab',
            )}
        >
            <td className="px-3 py-1.5 whitespace-nowrap">
                {editing ? (
                    <div
                        className="flex items-center"
                        style={{paddingLeft: `${Math.max(category.level - 1, 0) * 16}px`}}
                    >
                        <input
                            ref={inputRef}
                            value={name}
                            disabled={saving}
                            onChange={(event) => setName(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    saveEdit();
                                }
                                if (event.key === 'Escape') {
                                    event.preventDefault();
                                    cancelEdit();
                                }
                            }}
                            className="h-7 w-56 px-2 text-xs focus:ring-indigo-500 focus:border-indigo-500 block border-gray-300 rounded-md"
                        />
                        <button
                            type="button"
                            className="ml-2 py-1 px-2 text-xs font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200"
                            disabled={saving}
                            onClick={saveEdit}
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            className="ml-1 py-1 px-2 text-xs font-medium rounded-md bg-gray-200 text-red-500 hover:text-red-800"
                            onClick={cancelEdit}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <div
                        className="flex items-center text-xs font-medium text-gray-900"
                        style={{paddingLeft: `${Math.max(category.level - 1, 0) * 16}px`}}
                    >
                        {category.level > 1 && (
                            <ArrowTurnDownRightIcon className="h-4 w-4 mr-1.5 flex-shrink-0 text-gray-400" aria-hidden="true"/>
                        )}
                        {category.name}
                    </div>
                )}
            </td>
            <td className="px-3 py-1.5 whitespace-nowrap text-right text-xs font-medium">
                {showRevert && (
                    <button
                        type="button"
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={onRevert}
                    >
                        Revert
                    </button>
                )}
            </td>
            <td className="px-3 py-1.5 whitespace-nowrap text-right text-xs font-medium">
                <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="inline-flex items-center justify-center rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                        <EllipsisVerticalIcon className="h-4 w-4"/>
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute right-0 z-20 mt-1 w-40 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <Menu.Item>
                                {({active}) => (
                                    <button
                                        type="button"
                                        onClick={() => setEditing(true)}
                                        className={classNames(
                                            active ? 'bg-gray-100' : '',
                                            'block w-full px-3 py-1.5 text-left text-xs text-gray-700'
                                        )}
                                    >
                                        Rename
                                    </button>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({active}) => (
                                    <button
                                        type="button"
                                        onClick={() => onDelete(category)}
                                        className={classNames(
                                            active ? 'bg-gray-100' : '',
                                            'block w-full px-3 py-1.5 text-left text-xs text-red-700'
                                        )}
                                    >
                                        Delete
                                    </button>
                                )}
                            </Menu.Item>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </td>
        </tr>
    );
};

export default CategoryTreeItem;
